import { useState, useEffect, useCallback } from 'react'
import { pdf } from '@react-pdf/renderer'
import PDFRenderer from '@components/PDFRenderer/PDFRenderer'
import { getPlatform } from '@lib/telegram'

interface PDFData {
    transactionId: string
    transactionDate: string
    amount: string
    bank: string
    accountNumber: string
    accountName: string
    status: string
    fees: string
    totalAmount: string
}

interface CachedPDF {
    transactionId: string
    blob: Blob
    fileName: string
}

export const usePDFShare = (data: PDFData | null, isLoading: boolean = false) => {
    const [cachedPDF, setCachedPDF] = useState<CachedPDF | null>(null)
    const [isShareLoading, setIsShareLoading] = useState(false)

    const generatePDF = useCallback(async (): Promise<CachedPDF | null> => {
        if (!data) {
            return null
        }

        try {
            const blob = await pdf(<PDFRenderer
                transactionId={data.transactionId}
                transactionDate={data.transactionDate}
                amount={data.amount}
                bank={data.bank}
                accountNumber={data.accountNumber}
                accountName={data.accountName}
                status={data.status}
                fees={data.fees || '0'}
                totalAmount={data.totalAmount || '0'}
            />).toBlob()

            const fileName = `j-wallet-payment-receipt-${new Date().getTime()}.pdf`
            const pdfData = { transactionId: data.transactionId, blob, fileName }
            setCachedPDF(pdfData)

            return pdfData
        } catch (error) {
            throw error
        }
    }, [data])

    // Clear cache when transaction changes
    useEffect(() => {
        if (data && cachedPDF && cachedPDF.transactionId !== data.transactionId) {
            setCachedPDF(null)
        }
    }, [data?.transactionId, cachedPDF])

    // Pre-generate PDF when data is available
    useEffect(() => {
        if (data && (!cachedPDF || cachedPDF.transactionId !== data.transactionId) && !isLoading) {
            generatePDF()
        }
    }, [data?.transactionId, isLoading, cachedPDF, generatePDF])

    const shareOrDownloadPDF = useCallback(async () => {
        const platform = getPlatform()

        try {
            setIsShareLoading(true)

            // Use cached PDF if valid for current transaction, otherwise generate new one
            const currentPDF = (cachedPDF && cachedPDF.transactionId === data?.transactionId)
                ? cachedPDF
                : await generatePDF()

            if (!currentPDF) {
                return
            }

            const { blob, fileName } = currentPDF

            // iOS: Use Web Share API
            if (platform === 'ios' || platform === 'tdesktop') {
                if (navigator.share && navigator.canShare) {
                    const file = new File([blob], fileName, { type: 'application/pdf' })

                    const canShareFiles = navigator.canShare({ files: [file] })

                    if (canShareFiles) {
                        await navigator.share({
                            files: [file],
                        })
                        return
                    }
                }
            }

            // Android: Use Telegram-specific methods
            if (platform === 'android') {
                // Convert blob to data URL for Telegram APIs
                const reader = new FileReader()
                const dataUrl = await new Promise<string>((resolve, reject) => {
                    reader.onloadend = () => resolve(reader.result as string)
                    reader.onerror = reject
                    reader.readAsDataURL(blob)
                })

                // Method 1: Try Telegram openLink with data URL
                if (window.Telegram?.WebApp?.openLink) {
                    try {
                        window.Telegram.WebApp.openLink(dataUrl, { try_instant_view: false })
                        return
                    } catch (error) {
                        // Continue to next method
                    }
                }

                // Method 2: Try Telegram downloadFile
                if (window.Telegram?.WebApp?.downloadFile) {
                    try {
                        window.Telegram.WebApp.downloadFile({
                            url: dataUrl,
                            file_name: fileName
                        }, (isDownloading) => {})
                        return
                    } catch (error) {
                        // Continue to next method
                    }
                }

                // Method 3: Try opening in new window with PDF viewer
                try {
                    const pdfWindow = window.open('', '_blank')
                    if (pdfWindow) {
                        pdfWindow.document.write(`
                            <html>
                                <head><title>Payment Receipt</title></head>
                                <body style="margin:0">
                                    <iframe src="${dataUrl}" style="border:none;width:100%;height:100vh"></iframe>
                                </body>
                            </html>
                        `)
                        pdfWindow.document.close()
                        return
                    }
                } catch (error) {
                    // Continue to fallback
                }
            }

            // Fallback for unknown platforms or if all methods fail
            const url = URL.createObjectURL(blob)

            const link = document.createElement('a')
            link.href = url
            link.download = fileName
            document.body.appendChild(link)

            link.click()

            document.body.removeChild(link)
            URL.revokeObjectURL(url)

        } catch (error) {
            // Silent fail
        } finally {
            setIsShareLoading(false)
        }
    }, [cachedPDF, generatePDF, data?.transactionId])

    return {
        shareOrDownloadPDF,
        isShareLoading,
        isPDFReady: !!cachedPDF && cachedPDF.transactionId === data?.transactionId
    }
}